import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { getAllSlugs, getCmsPage } from "@/lib/api";

interface SolItem {
  slug: string;
  title: string;
  description?: string | null;
}

export default async function SolutionsListFallback() {
  let slugsResponse;
  try {
    slugsResponse = await getAllSlugs();
  } catch (e) {
    return null;
  }

  const pages = Array.isArray(slugsResponse.pages) ? slugsResponse.pages.filter((s) => s.startsWith("solutions-")) : [];
  if (pages.length === 0) return null;

  const items: SolItem[] = (
    await Promise.all(
      pages.map(async (p) => {
        try {
          const data = await getCmsPage(p);
          const pageData = data && !Array.isArray(data) && "title" in data ? (data as Record<string, unknown>) : null;
          const slug = p.replace(/^solutions-/, "");
          return {
            slug,
            title: (pageData?.title as string) || slug,
            description: (pageData?.description as string) || "",
          } as SolItem;
        } catch {
          return null;
        }
      })
    )
  ).filter(Boolean) as SolItem[];

  if (!items || items.length === 0) return null;

  return (
    <section className="py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-foreground">Explore Solutions</h2>
          <p className="mt-3 text-lg text-muted-foreground">Choose a solution to see recommended features and workflows.</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <Link key={it.slug} href={`/solutions/${it.slug}`} className="group block h-full">
              <Card className="h-full overflow-hidden border-border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-foreground group-hover:text-primary">{it.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-muted-foreground">{it.description}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                    Learn More <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
